import { createBrowserRouter, Navigate, useParams, type RouteObject } from 'react-router-dom';

import { STOREFRONT_PATHS } from '@rheora/enrollment-contract';

import { FunnelStepGate } from './module/shop/components/FunnelStepGate';
import type { FunnelStepId } from './module/shop/services/funnelSteps';
import { StorefrontLayout } from './module/shop/layout/StorefrontLayout';
import { FunnelHydrationGate } from './module/shop/state/FunnelHydrationGate';
import { CreditPage } from './module/shop/pages/CreditPage';
import { EstimatePage } from './module/shop/pages/EstimatePage';
import { LandingPage } from './module/shop/pages/LandingPage';
import { MeterPlansPage } from './module/shop/pages/MeterPlansPage';
import { OfferEntryPage } from './module/shop/pages/OfferEntryPage';
import { OfferUnavailablePage } from './module/shop/pages/OfferUnavailablePage';
import { ResumeConfirmPage } from './module/shop/pages/ResumeConfirmPage';
import { ResumeContinuePage } from './module/shop/pages/ResumeContinuePage';
import { ResumeExpiredPage } from './module/shop/pages/ResumeExpiredPage';
import { PlanDetailPage } from './module/shop/pages/PlanDetailPage';
import { SignPage } from './module/shop/pages/SignPage';
import { NewServicePendingPage } from './module/shop/pages/NewServicePendingPage';
import { StatusPage } from './module/shop/pages/StatusPage';

/**
 * Storefront routes:
 *   /                    — marketing landing page + the meter method chooser (the SOLE shop entry)
 *   /plans               — DEAD (old plan-first StartGate shelf) → redirects to the landing chooser
 *   /plan/:slug          — plan detail; `?src=ptc` is the Power to Choose deep link (AC1)
 *   /shop/plans          — SHOP-5A meter-filtered shelf (needs a resolved meter)
 *   /plan/:slug/credit   — E-SF05 identity → credit → deposit decision (candidate from FunnelProvider)
 *   /plan/:slug/estimate — E-SF04 personalization "your estimated bill" (?candidate=<id>)
 *   /plan/:slug/sign     — E-SF06 consent, click-wrap sign & accept (?candidate=<id>)
 *   /status/:token       — E-SF07 anonymous enrollment status page (email link; no login)
 *   /offer/:token        — E-SF16 emailed offer entry; hands off to GET /bff/offer/:token
 *   /offer/unavailable   — E-SF16 the ONE failure page for every unusable offer link
 *   /resume/expired      — E-CRM04 the ONE failure page for every unusable resume link
 *   /resume/busy         — E-CRM04 the per-IP limit refused the click (link untouched)
 *   /resume/confirm      — E-CRM04 phone check before an unverified lead is restored
 *   /resume/continue     — E-CRM04 every resume link lands here; Continue redeems it
 *                          (the link itself, /bff/resume/:token, goes straight to the BFF)
 * Anything else falls back to the shelf.
 */
/**
 * THE ROUTE TABLE — the single definition of what this app serves.
 *
 * ⚠️ EXPORTED SO TESTS CAN ASSERT AGAINST THE REAL THING. It used to be an inline
 * argument to `createBrowserRouter`, which meant a test could only ever mount a
 * component at a path it invented for itself. That is not a theoretical hazard: it
 * produced a green `AddressPage` suite rendering at `/plan/:slug/address`, a path
 * this table has never contained (see the SHOP-6 note below). The suite passed and
 * proved nothing about the application. See `docs/defect-classes/test-route-drift.md`.
 *
 * `src/test/render.tsx` now validates every `route`/`path` a test claims against
 * this array, so the same mistake fails at the offending line instead of passing.
 */
export const storefrontRoutes: RouteObject[] = [
  {
    // The storefront app shell (branded header + trust strip + funnel stepper +
    // compliance footer) wraps every page as a layout route. Pages self-pad, so
    // the shell only supplies chrome around each page's own centered container.
    //
    // FunnelHydrationGate wraps the whole storefront and owns FunnelProvider. It
    // reads `GET /bff/funnel` ONCE on mount and does not render the layout until that
    // settles, so the provider is CONSTRUCTED already seeded — which is what makes a
    // browser refresh stop losing the shopper's funnel. A 401 (no session) settles
    // just like a 200 and renders an empty funnel; it never mints a session.
    //
    // The store holds the shopper's method, ZIP, opaque candidateId, the display-only
    // meter detail and the chosen usage — never the raw ESI ID (AC3: it stays
    // server-side, keyed to the funnel-session cookie), and never the SSN or DOB
    // (those are re-entered by design; see CreditPage).
    element: (
      <FunnelHydrationGate>
        <StorefrontLayout />
      </FunnelHydrationGate>
    ),
    children: [
      { index: true, element: <LandingPage /> },
      // DEAD plan-first shelf: the meter-first flow's only entry is the landing
      // method chooser, so `/plans` now redirects there. `PlansShelfPage.tsx` +
      // StartGate/ZipFilter are kept in the repo but UNLINKED (no route mounts them).
      { path: '/plans', element: <Navigate to="/" replace /> },
      // SHOP-5A — the meter-first, meter-filtered shelf (guarded: needs a resolved
      // meter + usage in FunnelProvider, else it redirects to the chooser).
      { path: '/shop/plans', element: <MeterPlansPage /> },
      { path: '/plan/:slug', element: <PlanDetailPage /> },
      // SHOP-6 (partial): the "Service address" step is removed — the meter is
      // resolved up front (meter step → FunnelProvider). `AddressPage.tsx` is kept
      // in the repo but unlinked; the funnel goes plan → credit → estimate → sign.
      // ⚠️ EACH FUNNEL STEP IS GATED ON WHAT THE SERVER CAN EVIDENCE (E-SF19 P1).
      // Direct-URL to a later step on an early session rendered the whole surface —
      // including, at `/sign`, a deposit outcome for a customer whose credit had
      // never run. `FunnelStepGate` wraps rather than hooks so the gated tree never
      // mounts and its (captcha-gated, upstream-hitting) queries never fire.
      { path: '/plan/:slug/credit', element: <GatedStep step="credit" element={<CreditPage />} /> },
      {
        path: '/plan/:slug/estimate',
        element: <GatedStep step="estimate" element={<EstimatePage />} />,
      },
      { path: '/plan/:slug/sign', element: <GatedStep step="sign" element={<SignPage />} /> },
      // From the SHARED constant, not a literal: the sign step's completion panel
      // links here too, and two hand-written copies of a route is how a customer
      // lands on a 404 immediately after signing a contract.
      { path: STOREFRONT_PATHS.status, element: <StatusPage /> },
      // E-SF20 — the emailed pending-service link. Same posture as the status
      // route above: anonymous, ref-addressed, and named from the SHARED constant
      // because the BFF builds this URL and this app serves it (neither can see
      // the other's source).
      { path: STOREFRONT_PATHS.newServiceStatus, element: <NewServicePendingPage /> },
      // E-SF16 — the emailed offer link. Quotation builds `<origin>/offer/<token>`
      // (`offer-url.ts`), a US-SHOP path: the ingress path-routes `/bff/*` to the
      // BFF and `/` here, so the customer's click lands in the SPA and this route
      // hands it off to `GET /bff/offer/:token`.
      //
      // ⚠️ `/offer/unavailable` IS DECLARED FIRST FOR READABILITY ONLY — react-router
      // ranks a static segment above a dynamic one regardless of order, so the
      // failure page always wins over `:token`. It is unreachable as a token in any
      // case: a real token is 43 URL-safe base64 characters.
      { path: STOREFRONT_PATHS.offerUnavailable, element: <OfferUnavailablePage /> },
      { path: STOREFRONT_PATHS.offerEntry, element: <OfferEntryPage /> },
      { path: STOREFRONT_PATHS.resumeExpired, element: <ResumeExpiredPage /> },
      { path: STOREFRONT_PATHS.resumeBusy, element: <ResumeExpiredPage variant="busy" /> },
      { path: STOREFRONT_PATHS.resumeConfirm, element: <ResumeConfirmPage /> },
      { path: STOREFRONT_PATHS.resumeContinue, element: <ResumeContinuePage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

/*
 * ⚠️ THE ANNOTATION IS LOAD-BEARING. Without it, `tsc` fails with TS2742: the
 * INFERRED type of `router` is react-router's internal `RemixRouter`, which under
 * pnpm resolves through a path like `.pnpm/@remix-run+router@1.23.2/…` that this
 * package cannot name. `ReturnType<typeof createBrowserRouter>` expresses exactly the
 * same type using only the direct `react-router-dom` dependency, so it is portable.
 *
 * Do not "simplify" this back to a bare `export const router = …`.
 */
export const router: ReturnType<typeof createBrowserRouter> =
  createBrowserRouter(storefrontRoutes);

/**
 * A funnel step, wrapped in its gate.
 *
 * Exists so the route table stays a table: `element: <GatedStep step="sign" … />`
 * reads as "this route is the sign step, gated", where an inline
 * `<FunnelStepGate step="sign" slug={…}><SignPage /></FunnelStepGate>` could not
 * be written at all — the slug is a ROUTE PARAM, so it is only readable from
 * inside a routed component. That is the whole reason this indirection exists.
 */
function GatedStep({
  step,
  element,
}: {
  readonly step: FunnelStepId;
  readonly element: JSX.Element;
}): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  return (
    <FunnelStepGate step={step} slug={slug}>
      {element}
    </FunnelStepGate>
  );
}
